import React from 'react';
import {
  Grid as MuiGrid,
  Box,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';

interface GridItemProps {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  children: React.ReactNode;
}

interface GridProps {
  columns?: number;
  spacing?: number;
  responsive?: boolean;
  center?: boolean;
  container?: boolean;
  alignItems?: 'flex-start' | 'center' | 'flex-end' | 'stretch' | 'baseline';
  justifyContent?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly';
  children: React.ReactNode;
}

/**
 * GridItem component for individual grid cells
 */
export const GridItem: React.FC<GridItemProps> = ({
  xs = 12,
  sm,
  md,
  lg,
  xl,
  children,
}) => {
  return (
    <MuiGrid item xs={xs} sm={sm} md={md} lg={lg} xl={xl}>
      {children}
    </MuiGrid>
  );
};

/**
 * Grid component for creating responsive layouts
 * Provides a flexible grid system for organizing content
 */
export const Grid: React.FC<GridProps> = ({
  columns = 12,
  spacing = 3,
  responsive = true,
  center = false,
  container = true,
  alignItems = 'stretch',
  justifyContent = 'flex-start',
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Adjust spacing for responsive design
  const responsiveSpacing = responsive
    ? isMobile
      ? Math.max(1, spacing - 1)
      : isTablet
      ? spacing
      : spacing
    : spacing;

  const gridContent = (
    <MuiGrid
      container
      spacing={responsiveSpacing}
      alignItems={alignItems}
      justifyContent={justifyContent}
    >
      {React.Children.map(children, (child) => {
        // If child is a GridItem, return it as is
        if (React.isValidElement(child) && child.type === GridItem) {
          return child;
        }
        // Otherwise, wrap it in a GridItem with default responsive behavior
        return (
          <MuiGrid item xs={12} sm={6} md={4} lg={3}>
            {child}
          </MuiGrid>
        );
      })}
    </MuiGrid>
  );

  if (container) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: center ? 4 : 0 }}>
          {gridContent}
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ py: center ? 4 : 0 }}>
      {gridContent}
    </Box>
  );
};

/**
 * Masonry Grid variant for Pinterest-style layouts
 */
export const MasonryGrid: React.FC<{
  columns?: number;
  spacing?: number;
  children: React.ReactNode;
}> = ({
  columns = 3,
  spacing = 2,
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const responsiveColumns = isMobile ? 1 : isTablet ? 2 : columns;

  return (
    <Box
      sx={{
        columnCount: responsiveColumns,
        columnGap: theme.spacing(spacing),
        '& > *': {
          breakInside: 'avoid',
          marginBottom: theme.spacing(spacing),
        },
      }}
    >
      {children}
    </Box>
  );
};

// Export all components
export default Grid;